<template>
  <div :class="$style.formContainer">
    <LoginCard header="Multi-Factor Authentication">
      <div :class="$style.subheader">
        {{ mfaDisplayText.subheaderText }}
      </div>

      <Alert v-model="errorMessage" />

      <form>
        <LoginActionButton
          :action="handleAuth"
          :isLoading="isLoading"
          :disabled="isDisabled"
        >
          {{ mfaDisplayText.buttonText }}
        </LoginActionButton>
        <span
          v-if="!isWebAuthnSupported"
          :class="$style.unsupportedError"
        >
          Security Keys are not supported on this browser.
        </span>
      </form>

      <ChooseMfa
        v-bind="$props"
        :selectedFactor="factor"
      />
    </LoginCard>
  </div>
</template>

<script>
// Frameworks
import { get } from 'lodash';

// Constants and Types
import { LoginConsts } from 'client/util/Constants';
import { WebAuthn } from 'client/containers/Login/MfaType';
import MfaConstants from 'client/containers/Login/MfaConstants';

// Router
import { UserRoutes } from 'client/routes/RouteGenerator';

// Services
import webAuthnService from 'client/services/webAuthnService';

// Utils
import { webAuthnSupported } from 'client/util/FeatureSupport';
import loginUtil from 'client/components/mixins/loginUtil';

// Components
import ChooseMfa from 'client/containers/Login/ChooseMfa.vue';
import LoginCard from 'client/components/LoginCard.vue';

const { resetPasswordUserForm } = UserRoutes;
const { responseMessages } = LoginConsts;
const { passwordResetError } = responseMessages;

export default {
  name: 'VerifyWebAuthN',

  components: {
    ChooseMfa,
    LoginCard,
  },

  props: {
    challenge: {
      type: Object,
      default: null,
    },
    context: {
      type: String,
      default: '',
    },
    factor: {
      type: WebAuthn,
      required: true,
    },
    factors: {
      type: Array,
      default: () => ([]),
    },
    mfaDisplayText: {
      type: Object,
      default: () => ({}),
    },
    redirectTo: {
      type: String,
      default: '',
    },
    passwordResetInfo: {
      type: Object,
      default: null,
    },
    shouldPromptImmediately: {
      type: Boolean,
      default: false,
    },
  },

  mixins: [loginUtil],

  available: MfaConstants.mfaStatus.available,

  webAuthnService,

  mounted() {
    const { shouldPromptImmediately } = this;

    if (shouldPromptImmediately && this.isWebAuthnSupported) {
      this.handleAuth();
    }
  },

  computed: {
    isDisabled() {
      return !this.isWebAuthnSupported || this.isLoading;
    },

    isWebAuthnSupported() {
      return webAuthnSupported();
    },
  },

  methods: {
    handleError(error) {
      const statusCode = get(error, 'response.status');

      // 400 response indicates either mfa has expired or password doesnt meet org reqs
      if (this.context === LoginConsts.passwordReset && statusCode === 400) {
        const message = get(error, 'response.data.message', passwordResetError);
        const confirmationCode = get(this.passwordResetInfo, 'confirmationCode');

        this.redirect(`${resetPasswordUserForm}&key=${confirmationCode}&error=${message}`);
      } else {
        this.setErrorMessage(responseMessages.webauthnError);
      }
    },

    async handleAuth() {
      this.setRequestState();

      try {
        await this.$options
          .webAuthnService
          .authenticate(this.passwordResetInfo, this.challenge.webauthn);

        if (this.context === LoginConsts.sso) {
          this.redirect(UserRoutes.ssoRedirectUrl(this.redirectTo));
          return;
        }

        if (this.context === LoginConsts.oauth) {
          this.redirect(UserRoutes.oauthRedirectUrl(this.redirectTo));
          return;
        }

        this.redirect(this.redirectTo);
      } catch (err) {
        this.handleError(err);
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style module>
@import '../css/login-components.css';

.subheader {
  font-size: 0.75rem;
  font-weight: 600;
}

.unsupportedError {
  display: block;
  font-size: 0.75rem;
}
</style>
